import { type AxiosResponse } from 'axios';

import { handleApiError, handleValidationError } from '../utils/errorHandling';

import { getApiClient, getDefaultHeaders } from './httpClient';

export interface ApiMediaFile {
  product: string;
  sha256: string;
  fileName: string;
  fileId: string;
  fileType: string;
  uploadedAt: string;
  folder: string;
  tag1: string;
  description: string;
  tag2: string;
  s3Key: string;
  title: string;
  content: string;
}

export interface ApiMediaResponse {
  files: ApiMediaFile[];
}

export interface PresignedUrlResponse {
  url: string;
}

/**
 * Media API Service - Handles API interactions for media files
 *
 * This service provides functions to fetch media files and presigned URLs
 * from the backend API.
 */
export const mediaApiService = {
  /**
   * Get files for a selected tab
   *
   * @param product - The product identifier
   * @param folder - The folder to fetch files from
   * @returns A promise that resolves to an array of media files
   */
  getTabFiles: async (product: string, folder: string): Promise<ApiMediaFile[]> => {
    const { headers } = await getDefaultHeaders();
    try {
      const response: AxiosResponse<ApiMediaResponse> = await getApiClient().get('/files', {
        params: {
          product,
          folder,
        },
        headers: headers(),
      });
      return response.data.files;
    } catch (error) {
      throw handleApiError(error, 'getTabFiles', { product, folder });
    }
  },

  /**
   * Get presigned URL for a specific file
   *
   * @param fileId - The ID of the file to get a presigned URL for
   * @returns A promise that resolves to the presigned URL
   */
  getFilePresignedUrl: async (fileId: string): Promise<string> => {
    if (!fileId) {
      throw handleValidationError('fileId', 'getFilePresignedUrl');
    }

    try {
      const { headers } = await getDefaultHeaders();
      const response = await getApiClient().get(`/files/${fileId}/presigned-url`, {
        params: { purpose: 'view' },
        headers: {
          Accept: 'text/plain',
          ...headers(),
        },
        responseType: 'text',
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'getFilePresignedUrl', { fileId });
    }
  },
};
